import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, Utensils } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const DEFAULT_LOGIN_HERO = "https://images.pexels.com/photos/827528/pexels-photo-827528.jpeg";
const isDev = process.env.NODE_ENV === "development";

export default function AdminLogin() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  // Prefill only in development — production login must never expose default credentials
  const [email, setEmail] = useState(isDev ? "admin@resto.com" : "");
  const [password, setPassword] = useState(isDev ? "admin123" : "");
  const [submitting, setSubmitting] = useState(false);
  const [firstOutlet, setFirstOutlet] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/outlets/public");
        if (data && data[0]) setFirstOutlet(data[0]);
      } catch {}
    })();
  }, []);

  if (!loading && user) return <Navigate to="/admin" replace />;

  const brandName = firstOutlet?.name || "Meja&Menu";
  const brandTagline = firstOutlet?.tagline || "Dashboard untuk mengelola menu, meja, dan pesanan realtime.";
  const brandLogo = firstOutlet?.logo_file_id ? fileUrl(firstOutlet.logo_file_id) : (firstOutlet?.logo_url || "");
  const heroSrc = firstOutlet?.login_image_file_id
    ? fileUrl(firstOutlet.login_image_file_id)
    : (firstOutlet?.login_image_url || DEFAULT_LOGIN_HERO);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Login berhasil");
      navigate("/admin");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Login gagal"));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative">
        <img src={heroSrc}
             alt="Restaurant" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-foreground/50"></div>
        <div className="relative p-10 h-full flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white grid place-items-center overflow-hidden">
              {brandLogo ? <img src={brandLogo} alt="" className="w-full h-full object-contain p-1"/> : <Utensils className="w-5 h-5 text-primary"/>}
            </div>
            <span className="font-display font-bold text-lg">{brandName}</span>
          </div>
          <div>
            <h2 className="font-display text-4xl font-bold leading-tight">Kelola pesanan.<br/>Kuasai dapur.</h2>
            <p className="text-white/80 mt-3 max-w-sm">{brandTagline}</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-8 bg-background">
        <form onSubmit={submit} className="surface-card w-full max-w-md space-y-6 p-6 sm:p-8" data-testid="admin-login-form">
          {/* Mobile brand */}
          <div className="md:hidden flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-white border border-border grid place-items-center overflow-hidden">
              {brandLogo ? <img src={brandLogo} alt="" className="w-full h-full object-contain p-1"/> : <Utensils className="w-5 h-5 text-primary"/>}
            </div>
            <span className="font-display font-bold">{brandName}</span>
          </div>
          <div>
            <p className="eyebrow mb-2">Ruang Pengelolaan</p>
            <h1 className="font-display text-3xl md:text-4xl font-semibold text-teak">Login Admin</h1>
            <p className="text-sm text-muted-foreground mt-1">Masuk untuk mengelola restoran Anda.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="login-email">Email</Label>
            <Input id="login-email" autoComplete="username" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                   className="bg-white h-11" data-testid="login-email-input"/>
          </div>
          <div className="space-y-2">
            <Label htmlFor="login-password">Password</Label>
            <Input id="login-password" autoComplete="current-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                   className="bg-white h-11" data-testid="login-password-input"/>
          </div>
          <Button type="submit" disabled={submitting} className="w-full h-11 rounded-full" data-testid="login-submit-btn">
            {submitting ? "Loading..." : (<><LogIn className="w-4 h-4 mr-2" /> Masuk</>)}
          </Button>
          {isDev && (
            <p className="text-xs text-muted-foreground text-center" data-testid="dev-credentials-hint">
              Default: admin@resto.com / admin123 (development only)
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
